import type { BadgeProps } from '@wundo/ui';
import type { OutreachQueueEntry, OutreachQueueKind } from '@/lib/outreachQueue.ts';

/**
 * When a task fires, written in the user's own timezone.
 *
 * The queue is read to answer "what will this person get, and when do they get
 * it" — so every instant on the page is theirs, not the reader's.
 */
export function formatFireTime(iso: string | null, timezone?: string): string {
  if (!iso) return '—';

  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(timezone ? { timeZone: timezone } : {}),
  });
}

/** How far off it is, in the coarsest unit that still says something. */
export function relativeFromNow(iso: string | null): string {
  if (!iso) return '—';

  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const future = ms > 0;

  const minutes = Math.round(abs / 60_000);
  if (minutes < 1) return future ? 'in <1m' : 'now';
  if (minutes < 60) return future ? `in ${minutes}m` : `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return future ? `in ${hours}h` : `${hours}h ago`;

  const days = Math.round(hours / 24);
  return future ? `in ${days}d` : `${days}d ago`;
}

export function kindLabel(kind: OutreachQueueKind): string {
  if (kind === 'recommendation') return 'recommendation';
  if (kind === 'reminder') return 'reminder';
  return 'check-in';
}

/* Three kinds, three tones — the badge is what tells them apart at a glance. */
export function kindTone(kind: OutreachQueueKind): NonNullable<BadgeProps['tone']> {
  if (kind === 'recommendation') return 'info';
  if (kind === 'reminder') return 'warning';
  return 'neutral';
}

/** The name, falling back to the address and then to a readable id stub. */
export function outreachUserName(user: OutreachQueueEntry['user']): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name || user.email || user.id.slice(0, 8);
}

/** The one line of the payload worth putting in a table cell. */
export function payloadExcerpt(entry: OutreachQueueEntry): string {
  if (entry.kind === 'recommendation') return entry.payload.recommendation_reason ?? '';
  if (entry.kind === 'reminder') return entry.payload.reminder_content ?? '';
  return entry.payload.rrule_human ?? entry.payload.rrule ?? '';
}
